import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { LayoutGrid, LogOut, Settings, UserRound, Eye, EyeOff } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { logout } from "@/lib/auth";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useWorkspace } from "@/workspace/WorkspaceProvider";

export function UserMenu() {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    setMounted(true);
  }, []);

  const { email, displayName, roles } = useCurrentUser();
  const { workspace, isSuspended, suspendForSession, resumeFromSuspension } = useWorkspace();

  if (!mounted) return null;

  const handleLogout = () => {
    toast.success("Logged out successfully");
    logout();
  };

  // Match production header: comma-separated, uppercased role names
  const roleLabel = roles.join(", ");

  // Workspace state shown in the avatar badge + dropdown header. When a
  // workspace is active we surface its name (truncated for the badge) so
  // power users can confirm at a glance which view is currently driving the
  // console. When the user has temporarily suspended the workspace ("Use
  // full console") we show that explicitly rather than hiding the chip.
  const activeWorkspaceName = workspace?.name ?? null;
  const showBadge = Boolean(activeWorkspaceName) || isSuspended;
  const badgeLabel = isSuspended ? "Full console" : activeWorkspaceName ?? "";
  const badgeTooltip = isSuspended
    ? "Workspace suspended for this session. Click your avatar to resume."
    : activeWorkspaceName
      ? `Active workspace: ${activeWorkspaceName}`
      : "";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="group relative">
          <Button
            variant="ghost"
            className={`relative h-10 p-0 rounded-full transition-all duration-300 ease-out overflow-hidden ${
              !isMobile ? "group-hover:pr-3 group-data-[state=open]:pr-3" : ""
            }`}
          >
            <div className="flex items-center gap-2">
              <div className="relative flex-shrink-0">
                <Avatar className="h-8 w-8">
                  <AvatarImage
                    src={`https://api.dicebear.com/7.x/lorelei/svg?seed=${email || "default"}`}
                    alt="User avatar"
                  />
                  <AvatarFallback>
                    <UserRound className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
                {/* Always-visible workspace indicator dot — small enough not to
                    crowd the avatar, but anchored so the user can see at a
                    glance that a scoped view is in effect. The full label
                    appears on hover and inside the dropdown. */}
                {showBadge && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          aria-label={badgeTooltip}
                          className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-background ${
                            isSuspended ? "bg-muted-foreground" : "bg-primary"
                          }`}
                        />
                      </TooltipTrigger>
                      <TooltipContent side="bottom">{badgeTooltip}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              <div
                className={`flex flex-col items-start text-left transition-all duration-300 ease-out whitespace-nowrap overflow-hidden ${
                  !isMobile
                    ? "max-w-0 opacity-0 group-hover:max-w-[260px] group-hover:opacity-100 group-hover:pr-1 group-data-[state=open]:max-w-[260px] group-data-[state=open]:opacity-100 group-data-[state=open]:pr-1"
                    : "max-w-0 opacity-0"
                }`}
              >
                <span className="text-sm font-semibold text-foreground leading-tight">
                  {displayName ?? "Account"}
                </span>
                {roleLabel && (
                  <span className="text-[11px] font-medium text-muted-foreground leading-tight mt-0.5">
                    {roleLabel}
                  </span>
                )}
                {showBadge && (
                  <Badge
                    variant={isSuspended ? "outline" : "secondary"}
                    className="mt-1 h-4 px-1.5 text-[10px] font-medium gap-1 max-w-[220px]"
                  >
                    <LayoutGrid className="h-2.5 w-2.5 flex-shrink-0" />
                    <span className="truncate">{badgeLabel}</span>
                  </Badge>
                )}
              </div>
            </div>
            <span className="sr-only">User menu</span>
          </Button>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {showBadge && (
          <>
            <DropdownMenuLabel className="font-normal">
              <div className="flex items-start gap-2">
                <LayoutGrid className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    Active workspace
                  </p>
                  <p className="text-sm font-semibold text-foreground truncate">
                    {isSuspended ? "Full console (workspace paused)" : activeWorkspaceName}
                  </p>
                </div>
              </div>
            </DropdownMenuLabel>
            {isSuspended ? (
              <DropdownMenuItem onClick={resumeFromSuspension}>
                <Eye className="mr-2 h-4 w-4" />
                <span>Resume role workspace</span>
              </DropdownMenuItem>
            ) : (
              activeWorkspaceName && (
                <DropdownMenuItem onClick={suspendForSession}>
                  <EyeOff className="mr-2 h-4 w-4" />
                  <span>Use full console (this session)</span>
                </DropdownMenuItem>
              )
            )}
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onClick={() => navigate("/preferences")}>
          <Settings className="mr-2 h-4 w-4" />
          <span>Preferences</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
